import { CaretLeft, CaretRight } from '@phosphor-icons/react';
import { Children, useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { Button } from '@shared/ui/Button';
import styles from './HorizontalScroller.module.css';

/** A touch/keyboard scroll rail shared by category shortcuts and product collections. */
export function HorizontalScroller({ title, children, itemWidth = '190px', description, showControls = true }: {
  title: string; children: ReactNode; itemWidth?: string; description?: string; showControls?: boolean;
}) {
  const track = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState({ start: true, end: true });
  const count = Children.count(children);
  useEffect(() => {
    const element = track.current;
    if (!element || !showControls) return;
    const update = () => setEdges({ start: element.scrollLeft < 2, end: element.scrollLeft + element.clientWidth >= element.scrollWidth - 2 });
    update();
    const resize = new ResizeObserver(update); resize.observe(element);
    element.addEventListener('scroll', update, { passive: true });
    return () => { resize.disconnect(); element.removeEventListener('scroll', update); };
  }, [count, showControls]);
  const move = (direction: number) => track.current?.scrollBy({ left: direction * Math.max(150, track.current.clientWidth * .7), behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  return <section className={styles.section} aria-label={title}>
    <header className={styles.header}><div><h2>{title}</h2>{description && <p>{description}</p>}</div>
      {showControls && (!edges.start || !edges.end) && <div className={styles.arrows}><Button variant="secondary" aria-label={`Scroll ${title.toLowerCase()} left`} disabled={edges.start} onClick={() => move(-1)}><CaretLeft size={16} /></Button><Button variant="secondary" aria-label={`Scroll ${title.toLowerCase()} right`} disabled={edges.end} onClick={() => move(1)}><CaretRight size={16} /></Button></div>}
    </header>
    <div ref={track} className={styles.track} tabIndex={0} aria-label={`${title} scrollable list`} style={{ '--item-width': itemWidth } as CSSProperties}>
      {Children.map(children, child => child ? <div className={styles.item}>{child}</div> : null)}
    </div>
  </section>;
}
